import { useCallback } from "react";
import { createViewServiceProviderCoAMappings } from "@/ui/views";
import type { TSpCoaQuery, TSpCoAMappingView } from "@/ui/views";
import type { TCompositionDeps } from "./types";

export const wireServiceProviderCoAMappings = ({
    store: _store,
    useAppStore,
    api,
}: TCompositionDeps) => {
    const ViewServiceProviderCoAMappings = createViewServiceProviderCoAMappings({
        useCases: () => ({
            // Environment flag, not a permission: the feature does not exist in
            // this deployment at all when it is off.
            multiCriteriaCoaAvailable:
                useAppStore((state) => state.user)?.environmentFeatureFlags
                    ?.multi_criteria_coa_enabled ?? false,
            onLoadServiceProvider: useCallback(
                async (serviceProviderId: string) => {
                    const sp = await api.serviceProviders.get(serviceProviderId);
                    return {
                        id: sp.id,
                        name: sp.name,
                        code: sp.code,
                        coaMappingMode: sp.coa_mapping_mode ?? "single",
                    };
                },
                [api],
            ),
            onLoadCoAMappingsPage: useCallback(
                async (serviceProviderId: string, query: TSpCoaQuery, signal?: AbortSignal) => {
                    const response = await api.serviceProviders.listCoAMappings(
                        serviceProviderId,
                        {
                            ...query,
                            active_only: true,
                        },
                        signal,
                    );
                    const items: TSpCoAMappingView[] = response.items.map((m) => ({
                        id: m.id,
                        thirdAccountCode: m.third_account_code,
                        thirdAccountCode2: m.third_account_code_2 ?? null,
                        isFallback: m.is_fallback ?? false,
                        thirdAccountName: m.third_account_name,
                        yardiAccountId: m.yardi_account_id,
                        yardiAccountCode: m.yardi_account_code ?? null,
                        yardiAccountName: m.yardi_account_name ?? null,
                        level: m.level,
                        isInForce: m.is_in_force,
                        isActive: m.is_active,
                        createdAt: m.created_at,
                        updatedAt: m.updated_at,
                    }));
                    return {
                        items,
                        total: response.total,
                        totalUnfiltered: response.total_unfiltered ?? response.total,
                    };
                },
                [api],
            ),
        }),
    });

    return { ViewServiceProviderCoAMappings };
};
